import type { ReactNode } from 'react';

/**
 * 支持的语言枚举
 */
export enum I18nLanguage {
  en = 'en',
  ja = 'ja',
  de = 'de',
  fr = 'fr',
  es = 'es',
  it = 'it',
  nl = 'nl',
  tr = 'tr',
  zhCN = 'zh-cn',
  zhHK = 'zh-hk',
  ptPT = 'pt-pt',
  ptBR = 'pt-br',
  vi = 'vi',
}

/**
 * I18nConsumer Props
 */
export interface I18nConsumeProps {
  /** 语言加载器 */
  loaders: LocaleLoaders;
  /** 子组件 */
  children: ReactNode;
  /** 加载中显示的内容，默认不显示 */
  fallback?: ReactNode;
  /**
   * 命名空间（可选，但强烈推荐）
   * 用于缓存管理，相同命名空间的文案只加载一次
   * 不传 namespace 则每次都会加载文案
   * 推荐使用包名，如 'login', 'price-ui', 'changelog-modal'
   */
  namespace?: string;
}

/**
 * 国际化文案数据类型
 */
export type I18nLocaleData = Record<string, any>;

/**
 * 单个语言加载器类型
 *
 * 支持三种写法：
 * 1. 异步函数：() => import('./en.json')
 * 2. 同步函数：() => ({ key: 'value' })
 * 3. 直接传对象：{ key: 'value' }
 */
export type LocaleLoader =
  | (() => I18nLocaleData | Promise<I18nLocaleData>)
  | I18nLocaleData;

/**
 * 语言加载器映射类型
 */
export type LocaleLoaders = Record<string, LocaleLoader>;

/**
 * I18nContext 值类型
 */
export interface I18nContextValue {
  locale: string;
  setLocale: (locale: string) => void;
  fallbackLocale?: string;
}

/**
 * I18nProvider Props
 */
export interface I18nProviderProps {
  /** 子组件 */
  children: ReactNode;
  /** 当前语言（由应用层控制） */
  locale: string;
  /** 默认语言 */
  fallbackLocale?: string;
  /** 切换语言回调 */
  onLocaleChange?: (locale: string) => void;
  /** 调试模式 */
  debug?: boolean;
}
